import { Blockchain } from "../blockchain/types"

/**
 * The homepage's order, as launcher admins arrange it: which hero carousel
 * slides and Featured cards each page shows, and in which order.
 *
 * A page with nothing saved shows its default order. A saved page shows what
 * the admins have not arranged yet first (a collection published since, say),
 * in default order, then the saved entries in saved order, without the hidden
 * ones. A saved entry whose slide or card is gone is skipped.
 */

/** The pages admins arrange: All and the big chains. */
export const HOMEPAGE_LAYOUT_PAGES = [
  "ALL",
  Blockchain.ETHEREUM,
  Blockchain.SOLANA,
  Blockchain.ROBINHOOD,
  Blockchain.POLYGON,
  Blockchain.BASE,
  Blockchain.MEGAETH,
  Blockchain.RARI,
] as const

export type HomepageLayoutPage = (typeof HOMEPAGE_LAYOUT_PAGES)[number]

export function isHomepageLayoutPage(page: string): page is HomepageLayoutPage {
  return HOMEPAGE_LAYOUT_PAGES.some((each) => each === page)
}

export const HOMEPAGE_LAYOUT_SECTIONS = ["carousel", "featured"] as const

export type HomepageLayoutSection = (typeof HOMEPAGE_LAYOUT_SECTIONS)[number]

/**
 * One slide or card, by key:
 * - carousel: `launch:<collection id>`, `brand:<slug>`, `drop:<drop id>`;
 * - Featured: `collection:<collection id>`, `placeholder:<name>`.
 *
 * Two items with the same key (two "Coming soon" placeholders) are told
 * apart by their order: the second is `<key>#2`.
 */
export type HomepageLayoutEntry = {
  key: string
  hidden: boolean
}

/** A page's saved layout, as the launcher's admin page reads it. */
export interface HomepageLayout {
  page: HomepageLayoutPage
  /** Empty: the default order. */
  carousel: HomepageLayoutEntry[]
  featured: HomepageLayoutEntry[]
  updatedBy?: string
  /** ISO time of the last save. */
  updatedAt?: string
}

/** A card the Featured row can show, as the launcher's admin page lists it. */
export interface FeaturedLayoutCandidate {
  key: string
  name: string
  image: string
  mediaType?: "image" | "video"
  /** A launch, the curated list's, or a "Coming soon" placeholder. */
  source: "launch" | "curated" | "placeholder"
  /** A launch's id, for the admin page's Edit button. */
  collectionId?: string
  hidden: boolean
  /** False until an admin saved the page with it in. */
  arranged: boolean
}

/** A page's layout with the Featured cards it can show, for the admin page. */
export interface HomepageLayoutAdmin extends HomepageLayout {
  featuredCandidates: FeaturedLayoutCandidate[]
}

export const HOMEPAGE_LAYOUT_MAX_ENTRIES = 200

export const HOMEPAGE_LAYOUT_KEY_PATTERN =
  /^(launch|brand|drop|collection|placeholder):.{1,200}$/

/** A slide or card in the order the page shows it, hidden ones included. */
export interface ArrangedHomepageItem<T> {
  item: T
  key: string
  hidden: boolean
  /** False until an admin saved the page with it in. */
  arranged: boolean
}

/** Each item with its key; a repeated one numbered by its order (`#2`…). */
function keyItems<T>(items: readonly T[], keyOf: (item: T) => string) {
  const seen = new Map<string, number>()
  return items.map((item) => {
    const key = keyOf(item)
    const count = (seen.get(key) ?? 0) + 1
    seen.set(key, count)
    return { item, key: count === 1 ? key : `${key}#${count}` }
  })
}

/** Every item in the page's order, hidden ones included, for the admin page. */
export function arrangeHomepageItems<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
  entries: readonly HomepageLayoutEntry[] | undefined,
): ArrangedHomepageItem<T>[] {
  const keyed = keyItems(items, keyOf)
  if (!entries?.length) {
    return keyed.map((each) => ({ ...each, hidden: false, arranged: false }))
  }

  const saved = new Set(entries.map((entry) => entry.key))
  const byKey = new Map(keyed.map((each) => [each.key, each]))
  return [
    ...keyed
      .filter((each) => !saved.has(each.key))
      .map((each) => ({ ...each, hidden: false, arranged: false })),
    ...entries.flatMap((entry) => {
      const each = byKey.get(entry.key)
      return each ? [{ ...each, hidden: entry.hidden, arranged: true }] : []
    }),
  ]
}

/** What the page shows: the items in its order, without the hidden ones. */
export function applyHomepageLayout<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
  entries: readonly HomepageLayoutEntry[] | undefined,
): T[] {
  if (!entries?.length) return [...items]
  return arrangeHomepageItems(items, keyOf, entries)
    .filter((each) => !each.hidden)
    .map((each) => each.item)
}
